// Step Wizard data shapes — shared between framework + step modules.
//
// Backed by GET /v1/patrols/submissions/:id (apps/patrol-api/src/wizard.ts).

import type { ApiClientOptions } from "../../../lib/api-client.js";

/** Hand-off bag for any step component that needs to call the backend.
 *  Two implementations exist:
 *
 *   - **LIFF wizard** (`ScanWizardPage`) — auth is `{ jwt: session.session_token }`
 *     and paths point at `/v1/patrols/submissions/...` + `/v1/uploads/...`.
 *   - **Public scan wizard** (`ScanWizardPublicPage` after finalize) — auth is
 *     `{ scanSession: <token> }` and paths point at
 *     `/v1/patrols/scan/submissions/...` + `/v1/patrols/scan/uploads/...`.
 *
 *  Step components must NEVER reach into the LIFF context directly — go
 *  through this struct so the same component works under both auth modes. */
export interface WizardCtx {
  auth: ApiClientOptions;
  paths: WizardPaths;
}

export interface WizardPaths {
  getSubmission: (id: string) => string;
  patchSubmission: (id: string) => string;
  skipSubmission: (id: string) => string;
  uploadPhoto: () => string;
  uploadFile: () => string;
  uploadSignature: () => string;
  /** Build an authenticated URL for re-fetching a stored R2 key. The
   *  LIFF mode uses cookie-authed `/v1/patrols/evidence/<key>`; scan
   *  mode uses `/v1/patrols/scan/evidence/<key>` with the
   *  x-scan-session header. */
  evidenceUrl: (r2Key: string) => string;
}

/** Wizard ctx for the LIFF flow — passes the LIFF session JWT and the
 *  original submission / upload endpoints. */
export function liffWizardCtx(sessionToken: string): WizardCtx {
  return {
    auth: { jwt: sessionToken },
    paths: {
      getSubmission: (id) => `/v1/patrols/submissions/${encodeURIComponent(id)}`,
      patchSubmission: (id) => `/v1/patrols/submissions/${encodeURIComponent(id)}`,
      skipSubmission: (id) => `/v1/patrols/submissions/${encodeURIComponent(id)}/skip`,
      uploadPhoto: () => `/v1/uploads/photo`,
      uploadFile: () => `/v1/uploads/file`,
      uploadSignature: () => `/v1/uploads/signature`,
      evidenceUrl: (r2Key) => `/v1/patrols/evidence/${r2Key}`,
    },
  };
}

/** Wizard ctx for the public scan flow — auths via x-scan-session and
 *  targets the `/v1/patrols/scan/...` endpoint family. */
export function scanWizardCtx(token: string): WizardCtx {
  return {
    auth: { scanSession: token },
    paths: {
      getSubmission: (id) => `/v1/patrols/scan/submissions/${encodeURIComponent(id)}`,
      patchSubmission: (id) => `/v1/patrols/scan/submissions/${encodeURIComponent(id)}`,
      skipSubmission: (id) => `/v1/patrols/scan/submissions/${encodeURIComponent(id)}/skip`,
      uploadPhoto: () => `/v1/patrols/scan/uploads/photo`,
      uploadFile: () => `/v1/patrols/scan/uploads/file`,
      uploadSignature: () => `/v1/patrols/scan/uploads/signature`,
      evidenceUrl: (r2Key) => `/v1/patrols/scan/evidence/${r2Key}`,
    },
  };
}

export interface WizardSubmissionRow {
  id: string;
  tenant_id: string;
  task_id: string;
  assignment_id: string;
  patrol_event_id: string | null;
  patrol_session_id: string | null;
  officer_id: string;
  status: "in_progress" | "submitted" | "reviewed" | "expired" | "skipped";
  skip_reason: string | null;
  verify_device_credential_id: string | null;
  verify_device_assertion_at: number | null;
  verify_face_photo_r2_key: string | null;
  verify_face_match_score: number | null;
  verify_face_pose_index: number | null;
  verify_face_liveness_action: string | null;
  verify_face_liveness_at: number | null;
  verify_geo_lat: number | null;
  verify_geo_lon: number | null;
  verify_geo_distance_m: number | null;
  verify_geo_at: number | null;
  text_response: string | null;
  photo_r2_keys: string | null;
  file_r2_keys: string | null;
  signature_r2_key: string | null;
  started_at: number;
  submitted_at: number | null;
  reviewed_at: number | null;
}

export interface WizardTaskRow {
  id: string;
  name: string;
  description_short: string | null;
  instruction_doc: string | null;            // TipTap JSON (string)
  requires_text: number;
  requires_camera: number;
  requires_file: number;
  requires_signature: number;
  verify_device: number;
  verify_face: number;
  verify_geo: number;
  allow_skip: number;
}

export interface WizardAssignmentRow {
  id: string;
  target_kind: string;
  target_value: string;
  duration_minutes: number;
  grace_after_min: number;
}

export interface WizardLocationRow {
  id: string;
  label: string | null;
  lat: number | null;
  lon: number | null;
  geofence_radius_m: number | null;
  site_id: string | null;
}

export interface WizardData {
  submission: WizardSubmissionRow;
  task: WizardTaskRow;
  assignment: WizardAssignmentRow | null;
  location: WizardLocationRow | null;
  correlation_id?: string;
}

export type WizardStep = 1 | 2 | 3;

/** Whether a given step is required at all for this task. Steps with no
 *  enabled sub-toggles are skipped automatically (rendered as ✓ 略過). */
export function stepRequired(task: WizardTaskRow, step: WizardStep): boolean {
  // verify_geo re-enabled in scan-wizard-v2 PR-D. Pre-PR-C the union at
  // /scan/select-route ignored geo, so a wizard staring at task.verify_geo
  // =1 with no inherited timestamp from finalize would block forever. PR-C
  // closes that gap (geo joins required_verifications + finalize strict
  // gate), so it's safe to surface here.
  if (step === 1) return task.verify_device === 1 || task.verify_face === 1 || task.verify_geo === 1;
  if (step === 2) return task.requires_text === 1 || task.requires_camera === 1 || task.requires_file === 1;
  return task.requires_signature === 1;
}

/** Has the officer already completed this step (used to mark progress dots
 *  and to allow the wizard to resume). For step 1 we require ALL enabled
 *  sub-checks; step 2 requires every enabled evidence kind to be present;
 *  step 3 requires the signature blob.
 *
 *  `extras.pendingText` lets the caller (WizardRunner) inject local-only
 *  text state — text input is now controlled by the parent and not
 *  persisted to the server until handleSubmit fires, so server-side
 *  `s.text_response` lags behind the user's typing. Without this hook
 *  the「完成」button would stay disabled until the user manually saves,
 *  defeating the deferred-save design. */
export function stepCompleted(
  d: WizardData,
  step: WizardStep,
  extras?: { pendingText?: string },
): boolean {
  const t = d.task; const s = d.submission;
  if (step === 1) {
    if (t.verify_device === 1 && !s.verify_device_assertion_at) return false;
    if (t.verify_face === 1 && !s.verify_face_liveness_at) return false;
    if (t.verify_geo === 1 && !s.verify_geo_at) return false;
    return stepRequired(t, 1);
  }
  if (step === 2) {
    if (t.requires_text === 1) {
      const text = extras?.pendingText ?? s.text_response ?? "";
      if (text.trim().length === 0) return false;
    }
    if (t.requires_camera === 1) {
      const arr = parseR2KeyList(s.photo_r2_keys);
      if (arr.length === 0) return false;
    }
    if (t.requires_file === 1) {
      const arr = parseR2KeyList(s.file_r2_keys);
      if (arr.length === 0) return false;
    }
    return stepRequired(t, 2);
  }
  return Boolean(s.signature_r2_key);
}

export function parseR2KeyList(json: string | null): string[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
